import { useEffect, useRef, useState, type FormEvent } from "react";
import type { CoachingSessionsResult } from "../hooks/useAdminClaws";
import type { AdminOpenClaw } from "../types/openclaw";
import styles from "./ReassignModal.module.css";

type Props = {
  open: boolean;
  claw: AdminOpenClaw | null;
  onClose: () => void;
  onSubmit: (tenantId: string, remaining: number) => Promise<CoachingSessionsResult>;
};

const MIN = 0;
const MAX = 100;

export const CoachingSessionsModal = ({
  open,
  claw,
  onClose,
  onSubmit,
}: Props): JSX.Element | null => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    if (open && claw !== null && !d.open) {
      d.showModal();
      setValue(String(claw.coachingSessionsRemaining));
      setServerError(null);
      setSubmitting(false);
      window.setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 0);
    } else if (!open && d.open) {
      d.close();
    }
  }, [open, claw]);

  if (!open || claw === null) return null;

  const trimmed = value.trim();
  const parsed = trimmed === "" ? Number.NaN : Number(trimmed);
  const valid =
    Number.isInteger(parsed) && parsed >= MIN && parsed <= MAX;
  const unchanged = valid && parsed === claw.coachingSessionsRemaining;

  const handleSubmit = async (evt: FormEvent): Promise<void> => {
    evt.preventDefault();
    if (!valid || unchanged) return;
    setSubmitting(true);
    setServerError(null);
    const result = await onSubmit(claw.tenantId, parsed);
    setSubmitting(false);
    if (result.ok) {
      onClose();
      return;
    }
    setServerError(result.message);
  };

  const disabled = submitting || !valid || unchanged;

  return (
    <dialog
      ref={dialogRef}
      className={styles.dialog}
      onCancel={(evt) => {
        evt.preventDefault();
        if (!submitting) onClose();
      }}
    >
      <form method="dialog" className={styles.form} onSubmit={handleSubmit}>
        <header className={styles.header}>
          <h2 className={styles.title}>Coaching sessions</h2>
          <p className={styles.subtitle}>
            Set the remaining coaching sessions for{" "}
            <span className={styles.nameToken}>{claw.name}</span>. The
            customer sees this on their dashboard support panel.
          </p>
        </header>

        <label className={styles.field}>
          <span className={styles.label}>Sessions remaining</span>
          <input
            ref={inputRef}
            type="number"
            inputMode="numeric"
            min={MIN}
            max={MAX}
            step={1}
            className={styles.input}
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              setServerError(null);
            }}
            disabled={submitting}
            required
          />
          <span className={styles.hint}>
            Whole number between {MIN} and {MAX}. Currently{" "}
            <strong>{claw.coachingSessionsRemaining}</strong>.
          </span>
        </label>

        {serverError !== null ? (
          <div className={styles.errorBanner}>{serverError}</div>
        ) : null}

        <footer className={styles.footer}>
          <button
            type="button"
            className={styles.secondaryBtn}
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button type="submit" className={styles.primaryBtn} disabled={disabled}>
            {submitting ? "Saving…" : "Save"}
          </button>
        </footer>
      </form>
    </dialog>
  );
};
